import { forwardRef, TouchEvent, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { KeyValue } from '@mews/enums';
import { handleRefSplit, isMobileDevice } from '@optimus-web/common';
import { ActivableRenderer, Portal } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { useLockScroll } from '../hooks/useLockScroll';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { DrawerFooter } from './DrawerFooter';
import {
    DrawerBackdrop,
    DrawerCloseHandler,
    DrawerContainer,
    DrawerContent,
    DrawerDragBar,
    DrawerWrapper,
    getMinSwipeDistance,
} from './style';
import { CloseHandlerVariant, DrawerProps } from './types';
import { useDrawerHeight } from './useDrawerHeight';

const usePreventPullToRefresh = (isActive: boolean, contentRef: React.RefObject<HTMLDivElement>) => {
    useEffect(() => {
        if (isActive) {
            document.body.style.overscrollBehavior = 'none';

            const handleTouchMove = (e: globalThis.TouchEvent) => {
                const drawerContent = contentRef.current;
                if (!drawerContent) return;

                if (
                    drawerContent.contains(e.target as Node) &&
                    drawerContent.scrollHeight > drawerContent.clientHeight
                ) {
                    const isAtTop = drawerContent.scrollTop === 0;
                    const isPullingDown = e.touches[0].pageY > e.touches[0].clientY;

                    if (isAtTop && isPullingDown) {
                        e.preventDefault();
                    }
                    return;
                }

                e.preventDefault();
            };

            document.addEventListener('touchmove', handleTouchMove, { passive: false });

            return () => {
                document.body.style.overscrollBehavior = 'auto';
                document.removeEventListener('touchmove', handleTouchMove);
            };
        }
    }, [isActive, contentRef]);
};

const DrawerActivatorFunction = ActivableRenderer<HTMLDivElement, DrawerProps>({
    activePropName: 'open',
});

type VoidFn = () => void;

interface CloseHandlerProps {
    onClick?: VoidFn;
    onBackClick?: VoidFn;
}

const getDrawerCloseHandler = (closeHandler: CloseHandlerVariant, props: CloseHandlerProps) => {
    const closeButton = (
        <IconButton
            icon={IconName.CrossClose}
            variant="ghost"
            iconSize="medium"
            onClick={props.onClick}
            type="button"
        />
    );

    const backButton = (
        <IconButton
            icon={IconName.ArrowLeft}
            variant="ghost"
            iconSize="medium"
            onClick={closeHandler === 'backAndClose' ? props.onBackClick : props.onClick}
            type="button"
        />
    );

    switch (closeHandler) {
        case 'none':
            return null;
        case 'close':
            return closeButton;
        case 'back':
            return backButton;
        case 'backAndClose':
            return (
                <Stack fill distribution="space-between">
                    {backButton}
                    {closeButton}
                </Stack>
            );
        case 'drag':
            return <DrawerDragBar />;
    }
};

const DrawerComponent = forwardRef<HTMLDivElement, DrawerProps>(
    (
        {
            children,
            footer,
            height,
            open,
            onClose,
            onBackClick,
            dragBarLabel,
            portalContainerId,
            closeHandler = 'drag',
            lockScroll,
            withoutPadding = false,
            ...other
        },
        ref
    ) => {
        const [touchStart, setTouchStart] = useState<number | null>(null);
        const [touchEnd, setTouchEnd] = useState<number | null>(null);
        const [swipingInProgress, setSwipingInProgress] = useState(false);
        const [drawerIsCollapsed, setDrawerIsCollapsed] = useState(false);

        const drawerRef = useRef<HTMLDivElement>(null);
        const contentRef = useRef<HTMLDivElement>(null);

        const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
            setTouchEnd(null);
            setTouchStart(e.targetTouches[0].clientY);
        };

        const drawerHeight = useDrawerHeight();

        const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
            setTouchEnd(e.targetTouches[0].clientY);
        };

        const theme = useTheme();

        const onTouchEnd = () => {
            if (touchStart === null || touchEnd === null) return;
            const distance = touchEnd - touchStart;
            const isSwipe = getMinSwipeDistance({ theme }) < distance;

            if (drawerRef.current) {
                drawerRef.current.style.transform = `translate3d(0, ${isSwipe ? '100%' : '0'}, 0)`;
            }

            if (isSwipe && onClose) {
                setDrawerIsCollapsed(true);
                onClose();
            }
        };

        const swipeDistance = () => {
            if (!touchStart || !touchEnd || touchEnd - touchStart < 0) return 0;
            if (drawerRef.current) {
                setSwipingInProgress(true);
                drawerRef.current.style.transform = `translate3d(0, ${touchEnd - touchStart}px, 0)`;
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === KeyValue.Enter || event.key === KeyValue.Spacebar) {
                onClose?.();
            }
        };

        useEffect(() => {
            if (!drawerIsCollapsed && isMobileDevice()) {
                swipeDistance();
            }
        });

        useLockScroll(open, lockScroll);
        usePreventPullToRefresh(!!(open && isMobileDevice()), contentRef);

        return (
            <Portal containerId={portalContainerId} {...other}>
                <DrawerWrapper>
                    {open && <DrawerBackdrop onClick={onClose} />}
                    <DrawerContainer
                        ref={handleRefSplit({ ownRef: drawerRef, forwardedRef: ref })}
                        open={open}
                        height={height || drawerHeight}
                        maxHeight={drawerHeight}
                        data-test-drawer
                        swipingInProgress={swipingInProgress}
                    >
                        {closeHandler !== 'none' && (
                            <DrawerCloseHandler
                                onClick={closeHandler === 'drag' ? onClose : undefined}
                                onKeyDown={closeHandler === 'drag' ? handleKeyDown : undefined}
                                {...{
                                    closeHandler,
                                    ...(closeHandler === 'drag' && {
                                        tabIndex: 0,
                                        role: 'button',
                                        'aria-label': dragBarLabel,
                                    }),
                                }}
                                {...(isMobileDevice() && {
                                    onTouchStart,
                                    onTouchMove,
                                    onTouchEnd,
                                })}
                                data-test-drawer-close-handler
                            >
                                {getDrawerCloseHandler(closeHandler, {
                                    onClick: onClose,
                                    onBackClick,
                                })}
                            </DrawerCloseHandler>
                        )}
                        <DrawerContent
                            ref={contentRef}
                            withoutPadding={withoutPadding}
                            data-test-drawer-content
                            data-drawer-content
                            closeHandler={closeHandler}
                        >
                            {children}
                        </DrawerContent>
                        {footer ? <DrawerFooter {...footer} /> : null}
                    </DrawerContainer>
                </DrawerWrapper>
            </Portal>
        );
    }
);

export const Drawer = DrawerActivatorFunction(DrawerComponent);
